import { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  Search,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
} from 'lucide-react';
import { useDataService } from '@/lib/service-context';
import { Pagination } from '@/components/common/Pagination';
import { CitySelect } from '@/components/common/CitySelect';
import { formatTND } from '@/lib/format';
import { EMPTY_CLIENT_STATS, useProClientStats } from '@/hooks/use-pro-dashboard';
import type { CreateProClientInput, ProClient } from '@erp/data-access/interfaces';

type SortKey = 'name' | 'depense' | 'commission';
type SortDir = 'asc' | 'desc';

export function ClientsPage() {
  const { t } = useTranslation();
  const ds = useDataService();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<ProClient | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const query = useQuery({
    queryKey: ['pro-clients'],
    queryFn: () => ds.proClients.list(),
  });

  // ⚠ `pro_clients.depense` / `.commission` sont des colonnes mortes : aucun
  // trigger ne les alimente, elles valent 0 pour tout le monde. Les vrais
  // chiffres se recalculent depuis les documents.
  const { byClientId } = useProClientStats();
  const statsFor = (id: string) => byClientId.get(id) ?? EMPTY_CLIENT_STATS;

  const list = query.data ?? [];

  const filtered = useMemo(() => {
    let l = list;
    if (search.trim()) {
      const q = search.toLowerCase();
      l = l.filter((c) => {
        const fullName = `${c.prenom ?? ''} ${c.nom}`.toLowerCase();
        return (
          fullName.includes(q) ||
          (c.email ?? '').toLowerCase().includes(q) ||
          (c.telephone ?? '').includes(q) ||
          (c.ville ?? '').toLowerCase().includes(q)
        );
      });
    }
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      const get = (c: ProClient): string | number => {
        switch (sortKey) {
          case 'name':
            return `${c.prenom ?? ''} ${c.nom}`.toLowerCase();
          case 'depense':
            return statsFor(c.id).revenueTtc;
          case 'commission':
            return statsFor(c.id).commission;
        }
      };
      l = [...l].sort((a, b) => {
        const va = get(a);
        const vb = get(b);
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    }
    return l;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statsFor dérive de byClientId
  }, [list, search, sortKey, sortDir, byClientId]);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const onDelete = useMutation({
    mutationFn: (id: string) => ds.proClients.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pro-clients'] }),
  });

  const initials = (c: ProClient) => {
    const f = (c.prenom ?? c.nom).charAt(0).toUpperCase();
    const l = (c.nom ?? '').charAt(1)?.toUpperCase() ?? '';
    return f + l;
  };

  return (
    <div className="space-y-4">
      <div className="pro-card p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--pro-text-grey)' }}
            />
            <input
              type="search"
              placeholder={t('pro.clients.search_placeholder', 'Rechercher des clients')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-3 h-10 rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-orange-300"
              style={{ background: 'var(--pro-grey-subtle)', color: 'var(--pro-text-dark)' }}
            />
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-[14px] font-semibold text-white"
            style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
          >
            <Plus className="w-4 h-4" />
            {t('pro.clients.new', 'Nouveau client')}
          </button>
        </div>

        {query.isLoading ? (
          <div className="p-12 text-center" style={{ color: 'var(--pro-text-grey)' }}>
            …
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Users
              className="w-10 h-10 mx-auto mb-2"
              style={{ color: 'var(--pro-text-grey-light)' }}
            />
            <p style={{ color: 'var(--pro-text-grey)' }}>
              {t('pro.clients.empty', 'Aucun client trouvé')}
            </p>
          </div>
        ) : (
          <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {paged.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/clients/${encodeURIComponent(c.id)}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/clients/${encodeURIComponent(c.id)}`);
                  }
                }}
                className="rounded-xl p-4 space-y-3 cursor-pointer active:bg-orange-50/40"
                style={{ border: '1px solid var(--pro-line)' }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0"
                    style={{
                      background: 'var(--pro-grey-subtle)',
                      color: 'var(--pro-text-dark)',
                      fontFamily: 'Figtree',
                    }}
                  >
                    {initials(c)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[14px] font-medium truncate"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {[c.prenom, c.nom].filter(Boolean).join(' ')}
                    </div>
                    <div
                      className="text-[12px] truncate"
                      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
                    >
                      {c.email ?? '—'}
                    </div>
                  </div>
                  <RowMenu
                    open={openMenuId === c.id}
                    onToggle={() => setOpenMenuId(openMenuId === c.id ? null : c.id)}
                    onEdit={() => {
                      setOpenMenuId(null);
                      setEditing(c);
                    }}
                    onDelete={() => {
                      setOpenMenuId(null);
                      if (confirm(t('pro.clients.confirm_delete', 'Supprimer ce client ?'))) {
                        onDelete.mutate(c.id);
                      }
                    }}
                  />
                </div>
                <div
                  className="flex items-center justify-between gap-2 pt-3"
                  style={{ borderTop: '1px dashed var(--pro-line)' }}
                >
                  <div>
                    <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>
                      {t('pro.clients.total_spend', 'Dépenses totales')}
                    </div>
                    <div
                      className="text-[14px] font-medium"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {formatTND(statsFor(c.id).revenueTtc)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>
                      {t('pro.clients.total_commission', 'Commission totale')}
                    </div>
                    <div
                      className="text-[14px] font-semibold"
                      style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
                    >
                      {formatTND(statsFor(c.id).commission)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop: full table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--pro-line)' }}>
                  <SortableTh label={t('pro.clients.col_name', 'Clients')} sortKey="name" current={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label={t('pro.clients.total_spend', 'Dépenses totales')} sortKey="depense" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                  <SortableTh label={t('pro.clients.total_commission', 'Commission totale')} sortKey="commission" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                  <Th className="text-right" />
                </tr>
              </thead>
              <tbody>
                {paged.map((c) => (
                  <tr
                    key={c.id}
                    style={{ borderBottom: '1px solid var(--pro-line)' }}
                    className="hover:bg-orange-50/40 cursor-pointer"
                    onClick={() => navigate(`/clients/${encodeURIComponent(c.id)}`)}
                  >
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold shrink-0"
                          style={{
                            background: 'var(--pro-grey-subtle)',
                            color: 'var(--pro-text-dark)',
                            fontFamily: 'Figtree',
                          }}
                        >
                          {initials(c)}
                        </div>
                        <div className="min-w-0">
                          <div
                            className="text-[14px] font-medium truncate"
                            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                          >
                            {[c.prenom, c.nom].filter(Boolean).join(' ')}
                          </div>
                          <div
                            className="text-[12px] truncate"
                            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
                          >
                            {c.email ?? '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className="py-4 px-3 text-right text-[14px] font-medium"
                      style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                    >
                      {formatTND(statsFor(c.id).revenueTtc)}
                    </td>
                    <td
                      className="py-4 px-3 text-right text-[14px] font-semibold"
                      style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
                    >
                      {formatTND(statsFor(c.id).commission)}
                    </td>
                    <td className="py-4 px-3 text-right whitespace-nowrap">
                      <RowMenu
                        open={openMenuId === c.id}
                        onToggle={() => setOpenMenuId(openMenuId === c.id ? null : c.id)}
                        onEdit={() => {
                          setOpenMenuId(null);
                          setEditing(c);
                        }}
                        onDelete={() => {
                          setOpenMenuId(null);
                          if (confirm(t('pro.clients.confirm_delete', 'Supprimer ce client ?'))) {
                            onDelete.mutate(c.id);
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
      </div>

      {(creating || editing) && (
        <ClientFormModal
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function Th({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`text-left py-3 px-3 text-[12px] font-medium ${className ?? ''}`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}

function SortableTh({
  label,
  sortKey,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey | null;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = current === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      className={`py-3 px-3 text-[12px] font-medium select-none ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1"
        style={{ color: active ? 'var(--pro-text-dark)' : 'var(--pro-text-grey)' }}
      >
        {label}
        <Icon className="w-3 h-3" />
      </button>
    </th>
  );
}

function RowMenu({
  open,
  onToggle,
  onEdit,
  onDelete,
}: {
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onToggle]);
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label={t('pro.clients.actions', 'Actions')}
        className="w-8 h-8 inline-flex items-center justify-center rounded-lg"
        style={{
          color: 'var(--pro-text-grey)',
          background: open ? 'var(--pro-grey-subtle)' : 'transparent',
        }}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-44 rounded-lg z-10 pro-scale-in py-1"
          style={{
            background: 'white',
            border: '1px solid var(--pro-border)',
            boxShadow: '0 8px 24px rgba(13,13,18,0.08)',
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-neutral-50"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            <Pencil className="w-4 h-4" />
            {t('pro.clients.edit', 'Modifier')}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-red-50"
            style={{ color: 'var(--pro-danger)', fontFamily: 'Figtree' }}
          >
            <Trash2 className="w-4 h-4" />
            {t('pro.clients.delete', 'Supprimer')}
          </button>
        </div>
      )}
    </div>
  );
}

export function ClientFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: ProClient | null;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const ds = useDataService();
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateProClientInput>({
    nom: initial?.nom ?? '',
    prenom: initial?.prenom ?? '',
    email: initial?.email ?? '',
    telephone: initial?.telephone ?? '',
    adresse: initial?.adresse ?? '',
    ville: initial?.ville ?? '',
    cp: initial?.cp ?? '',
    complement: initial?.complement ?? '',
    notes: initial?.notes ?? '',
  });

  const save = useMutation({
    mutationFn: async () => {
      if (initial) await ds.proClients.update(initial.id, form);
      else await ds.proClients.create(form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro-clients'] });
      onSaved?.();
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 pro-fade-in" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 pro-scale-in">
        <h2
          className="text-[18px] font-semibold mb-4"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {initial
            ? t('pro.clients.edit_title', 'Modifier le client')
            : t('pro.clients.new_title', 'Nouveau client')}
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t('pro.clients.first_name', 'Prénom')}
              value={form.prenom ?? ''}
              onChange={(v) => setForm({ ...form, prenom: v })}
            />
            <Field
              label={t('pro.clients.last_name', 'Nom')}
              value={form.nom}
              onChange={(v) => setForm({ ...form, nom: v })}
              required
            />
          </div>
          <Field
            label={t('pro.clients.email', 'Email')}
            type="email"
            value={form.email ?? ''}
            onChange={(v) => setForm({ ...form, email: v })}
          />
          <Field
            label={t('pro.clients.phone', 'Téléphone')}
            value={form.telephone ?? ''}
            onChange={(v) => setForm({ ...form, telephone: v })}
            inputMode="tel"
          />
          <Field
            label={t('pro.clients.address', 'Adresse')}
            value={form.adresse ?? ''}
            onChange={(v) => setForm({ ...form, adresse: v })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t('pro.clients.postal_code', 'Code postal')}
              value={form.cp ?? ''}
              onChange={(v) => setForm({ ...form, cp: v })}
              inputMode="numeric"
              maxLength={4}
            />
            <CitySelect
              label={t('pro.clients.city', 'Ville')}
              value={form.ville ?? ''}
              onChange={(v) => setForm({ ...form, ville: v })}
              selectClassName="rounded-xl"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 h-11 rounded-xl text-[14px]"
              style={{ border: '1px solid var(--pro-border)', color: 'var(--pro-text-dark)' }}
            >
              {t('pro.common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={save.isPending || !form.nom.trim()}
              className="px-5 h-11 rounded-xl text-[14px] font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
            >
              {t('pro.common.save', 'Enregistrer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required,
  inputMode,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  inputMode?: 'tel' | 'numeric';
  maxLength?: number;
}) {
  return (
    <div>
      <label
        className="text-[14px] font-medium mb-1.5 block"
        style={{ color: 'var(--pro-text-muted)' }}
      >
        {label} {required && <span style={{ color: 'var(--pro-danger)' }}>*</span>}
      </label>
      <input
        type={type}
        required={required}
        inputMode={inputMode}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-orange-300"
        style={{
          border: '1px solid var(--pro-border)',
          color: 'var(--pro-text-dark)',
          background: 'white',
        }}
      />
    </div>
  );
}
